import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ChevronLeft, ChevronRight, Play, Pause, Newspaper, Image as ImageIcon } from "lucide-react";

/**
 * NewsSlideshow — auto-rotating hero slideshow that pulls the newest news
 * articles (with image_url) and the newest media items and stitches them
 * into one carousel. Pauses on hover, keyboard navigable, dot pagination.
 */
const AUTO_ROTATE_MS = 6000;

const NewsSlideshow = () => {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [nRes, mRes] = await Promise.all([
          axios.get(`${API}/news?limit=6`).catch(() => ({ data: [] })),
          axios.get(`${API}/media?limit=6`).catch(() => ({ data: [] })),
        ]);
        const news = (nRes.data || [])
          .filter((n) => n.image_url)
          .map((n) => ({
            kind: "news",
            id: n.article_id,
            image: n.image_url,
            title: n.title,
            description: n.excerpt || (n.content?.slice(0, 140) + "…"),
            category: n.category || "news",
            date: n.published_at || n.created_at,
            href: `/news/${n.article_id}`,
          }));
        const media = (mRes.data || [])
          .filter((m) => m.image_url || m.url)
          .map((m) => ({
            kind: "media",
            id: m.media_id,
            image: m.image_url || m.url,
            title: m.title || "Gallery",
            description: m.description || m.caption || "",
            category: "media",
            date: m.created_at,
            href: "/media",
          }));
        const merged = [...news, ...media].slice(0, 8);
        setSlides(merged);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const next = useCallback(() => setIndex((i) => (slides.length ? (i + 1) % slides.length : 0)), [slides.length]);
  const prev = useCallback(() => setIndex((i) => (slides.length ? (i - 1 + slides.length) % slides.length : 0)), [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    timerRef.current = setInterval(next, AUTO_ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, slides.length, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (loading) {
    return (
      <div className="relative w-full h-[420px] md:h-[560px] bg-gray-900 animate-pulse" data-testid="slideshow-loading" />
    );
  }

  if (!slides.length) return null;

  const current = slides[index];

  return (
    <section
      className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-[#09090b] group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="news-slideshow"
    >
      {/* Slides stacked; fade between them */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100 z-10" : "opacity-0 z-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />
        </div>
      ))}

      {/* Slide content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 md:px-8 h-full flex items-end pb-16 md:pb-24">
        <div className="max-w-2xl text-white">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#FFC72C] text-black text-[10px] font-heading uppercase tracking-widest font-bold">
              {current.kind === "media" ? <ImageIcon className="w-3 h-3" /> : <Newspaper className="w-3 h-3" />}
              {current.kind === "media" ? "Media" : current.category}
            </span>
            {current.date && (
              <span className="text-[10px] uppercase tracking-widest text-gray-300">
                {new Date(current.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
          <h1
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-black uppercase leading-tight mb-4"
            data-testid="slideshow-title"
          >
            <Link to={current.href} className="hover:text-[#FFC72C] transition-colors">
              {current.title}
            </Link>
          </h1>
          {current.description && (
            <p className="text-sm md:text-base text-gray-300 mb-6 line-clamp-3 max-w-xl">
              {current.description}
            </p>
          )}
          <Link
            to={current.href}
            className="inline-flex items-center gap-2 bg-[#FFC72C] text-black font-heading font-bold uppercase text-sm px-6 py-3 hover:bg-white transition-colors"
            data-testid="slideshow-cta"
          >
            {current.kind === "media" ? "View Gallery" : "Read Story"}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Controls */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 bg-black/50 hover:bg-[#FFC72C] hover:text-black text-white flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Previous slide"
        data-testid="slideshow-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 bg-black/50 hover:bg-[#FFC72C] hover:text-black text-white flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Next slide"
        data-testid="slideshow-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots + play/pause */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3">
        <div className="flex items-center gap-2" role="tablist">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-selected={i === index}
              className={`transition-all ${i === index ? "w-8 h-1.5 bg-[#FFC72C]" : "w-1.5 h-1.5 bg-white/60 hover:bg-white"}`}
              data-testid={`slideshow-dot-${i}`}
            />
          ))}
        </div>
        <button
          onClick={() => setPaused((v) => !v)}
          className="ml-3 w-7 h-7 flex items-center justify-center bg-black/40 hover:bg-black/70 text-white transition-colors"
          aria-label={paused ? "Play" : "Pause"}
          data-testid="slideshow-pause"
        >
          {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Auto-progress bar */}
      {!paused && slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-30">
          <div
            key={`${index}-${paused}`}
            className="h-full bg-[#FFC72C]"
            style={{ animation: `slideshowProgress ${AUTO_ROTATE_MS}ms linear forwards` }}
          />
        </div>
      )}
      <style>{`
        @keyframes slideshowProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default NewsSlideshow;
